import { useCallback, useEffect, useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import RatingPicker from "../../components/appraisal/RatingPicker";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_FEEDBACK } from "../../constants/api";

const EMPTY = { rating: null, comment: "", strengths: "", improvements: "", is_anonymous: false };

/**
 * The reviewer's own inbox: appraisals this person has been asked to comment on.
 *
 * Deliberately separate from My Appraisal — being asked to review a colleague
 * has nothing to do with your own review, and burying it there is how 360
 * requests go unanswered.
 */
const FeedbackRequests = () => {
    const { fetch, create } = useApiMethods();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_FEEDBACK, { mine: "true" });
            setRequests(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load your feedback requests");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async (row) => {
        const hasText = [form.comment, form.strengths, form.improvements]
            .some((v) => String(v || "").trim());
        if (!form.rating && !hasText) {
            ToastMsg("error", "Add a rating or a comment before submitting");
            return;
        }
        setSaving(true);
        const res = await create(APPRAISAL_FEEDBACK, {
            reviewer_id: row.id,
            is_anonymous: form.is_anonymous,
            feedback: [{
                rating: form.rating,
                comment: form.comment,
                strengths: form.strengths,
                improvements: form.improvements,
            }],
        });
        setSaving(false);
        if (res?.status === "success") {
            setOpenId(null); setForm(EMPTY);
            await load();
        }
    };

    const pending = requests.filter((r) => r.status !== "submitted");
    const done = requests.filter((r) => r.status === "submitted");

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={MessageSquare}
                title="Feedback Requests"
                subtitle="Colleagues who have asked for your feedback"
                totalRecords={pending.length}
            />

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mt-2">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                        <p className="font-medium text-foreground">No feedback requests</p>
                        <p className="text-sm mt-1">
                            When a colleague nominates you as a reviewer and their manager
                            approves the panel, their request appears here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {[...pending, ...done].map((r) => (
                            <div key={r.id} className={cn("border border-border rounded-lg p-3",
                                r.status === "submitted" && "opacity-70")}>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="font-medium">{r.employee_name}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {r.cycle_name}
                                            {` · ${String(r.relationship || "").replace("_", " ")}`}
                                            {r.invited_at && ` · asked ${dayjs(r.invited_at).format("DD-MM")}`}
                                        </div>
                                    </div>
                                    {r.status === "submitted" ? (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                            Submitted
                                        </span>
                                    ) : (
                                        <Button size="sm" variant="outline"
                                            onClick={() => { setOpenId(openId === r.id ? null : r.id); setForm(EMPTY); }}>
                                            {openId === r.id ? "Close" : "Give feedback"}
                                        </Button>
                                    )}
                                </div>

                                {openId === r.id && (
                                    <div className="mt-3 space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Overall rating (optional)
                                            </label>
                                            <RatingPicker value={form.rating}
                                                onChange={(v) => setForm({ ...form, rating: v })} />
                                        </div>
                                        <Textarea rows={2} placeholder="What stood out about working with them?"
                                            value={form.strengths}
                                            onChange={(e) => setForm({ ...form, strengths: e.target.value })} />
                                        <Textarea rows={2} placeholder="What could they do differently?"
                                            value={form.improvements}
                                            onChange={(e) => setForm({ ...form, improvements: e.target.value })} />
                                        <Textarea rows={2} placeholder="Anything else (optional)"
                                            value={form.comment}
                                            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
                                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="mt-0.5"
                                                checked={form.is_anonymous}
                                                onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })}
                                            />
                                            <span>
                                                Hide my name from {r.employee_name} and their manager
                                                <span className="block text-xs text-muted-foreground">
                                                    HR can still see it, so the channel can be audited.
                                                </span>
                                            </span>
                                        </label>
                                        <Button size="sm" disabled={saving} onClick={() => submit(r)}>
                                            <Send className="h-4 w-4 mr-1" /> Submit feedback
                                        </Button>
                                        <p className="text-xs text-muted-foreground">
                                            You can only submit once, and it cannot be edited afterwards.
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default FeedbackRequests;
